/**
 * Persists the inflated "display percent" used by the ingestion progress UI.
 *
 * Why this exists:
 * The ingestion progress label is a mix of the real server percent (polled every
 * few seconds) and a small fake animation that creeps above it for smoothness.
 * If we kept the inflated value only in component-local React state, navigating
 * away (unmounting the component) or refreshing the page would discard it,
 * causing the percentage to visibly drop back to the lower server value before
 * climbing again. We persist it here so the displayed value is monotonic across
 * remounts and reloads within the same tab.
 *
 * Storage layers:
 *  - In-memory `Map`: covers navigation within the SPA (no I/O on hot paths).
 *  - `sessionStorage`: covers a hard refresh of the same tab. We deliberately
 *    avoid `localStorage` so stale entries don't accumulate across sessions.
 *
 * Both layers are best-effort: `sessionStorage` may throw (private mode, quota,
 * disabled by policy) and is gracefully ignored.
 */

const STORAGE_KEY_PREFIX = 'ingestion-display-percent:' as const

const memoryCache = new Map<string, number>()

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }
    return window.sessionStorage
  } catch {
    return null
  }
}

function makeKey(sourceId: string): string {
  return `${STORAGE_KEY_PREFIX}${sourceId}`
}

export function readIngestionDisplayPercent(sourceId: string): number | null {
  const cached = memoryCache.get(sourceId)
  if (cached != null) {
    return cached
  }
  const storage = getStorage()
  if (storage === null) {
    return null
  }
  try {
    const raw = storage.getItem(makeKey(sourceId))
    if (raw === null) {
      return null
    }
    const parsed = Number(raw)
    if (!Number.isFinite(parsed)) {
      return null
    }
    memoryCache.set(sourceId, parsed)
    return parsed
  } catch {
    return null
  }
}

export function writeIngestionDisplayPercent(
  sourceId: string,
  value: number
): void {
  if (!Number.isFinite(value)) {
    return
  }
  memoryCache.set(sourceId, value)
  const storage = getStorage()
  if (storage === null) {
    return
  }
  try {
    storage.setItem(makeKey(sourceId), String(value))
  } catch {
    // Storage may be full or disabled; the in-memory cache still works for
    // navigation within the tab, so we silently fall back to that.
  }
}

export function clearIngestionDisplayPercent(sourceId: string): void {
  memoryCache.delete(sourceId)
  const storage = getStorage()
  if (storage === null) {
    return
  }
  try {
    storage.removeItem(makeKey(sourceId))
  } catch {
    // best-effort cleanup
  }
}

/** Test-only helper to reset the in-memory cache between cases. */
export function __resetIngestionDisplayPercentStoreForTests(): void {
  memoryCache.clear()
}
